import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Modal, Button } from 'react-bootstrap';
import { getCroppedBlob, PixelCrop } from '../utils/cropImage';

interface Props {
    imageSrc: string;
    aspect: number;
    outputWidth: number;
    outputHeight: number;
    title: string;
    onCancel: () => void;
    onConfirm: (blob: Blob) => Promise<void> | void;
}

const ImageCropModal: React.FC<Props> = ({
    imageSrc, aspect, outputWidth, outputHeight, title, onCancel, onConfirm,
}) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [pixelCrop, setPixelCrop] = useState<PixelCrop | null>(null);
    const [saving, setSaving] = useState(false);

    const onCropComplete = useCallback((_: unknown, area: PixelCrop) => {
        setPixelCrop(area);
    }, []);

    const handleConfirm = async () => {
        if (!pixelCrop) return;
        setSaving(true);
        try {
            const blob = await getCroppedBlob(imageSrc, pixelCrop, outputWidth, outputHeight);
            await onConfirm(blob);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show onHide={onCancel} size="lg" backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div style={{
                    position: 'relative', width: '100%', height: 400,
                    background: '#000', borderRadius: 6, overflow: 'hidden',
                }}>
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                    />
                </div>
                <div className="mt-3">
                    <label className="form-label small">
                        Zoom: <strong>{zoom.toFixed(2)}×</strong>
                    </label>
                    <input
                        type="range" min={1} max={3} step={0.01}
                        value={zoom}
                        onChange={e => setZoom(Number(e.target.value))}
                        className="form-range"
                    />
                </div>
                <div className="small text-body-secondary">
                    Output: {outputWidth} × {outputHeight} px (PNG)
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onCancel} disabled={saving}>
                    Annulla
                </Button>
                <Button variant="primary" onClick={handleConfirm} disabled={saving || !pixelCrop}>
                    {saving ? <><span className="spinner-border spinner-border-sm me-2" />Salvo…</> : 'Salva'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ImageCropModal;
